import {
	alephToast,
	AppEnum,
	BulkActions,
	Button,
	ButtonAppearance,
	GenericStatus,
	Group,
	LoaderBars,
	NotificationToast,
	NotificationType,
	Paragraph,
	PauseIcon,
	PlayIcon,
	Tooltip,
	useUrlParams,
	TrashCanIcon,
} from '@aleph/shared-components';
import { LEVEL_ID_FILTER } from 'wb-spotify/app/routes/superGrid/actionRow/filters/utils';
import { GridApi, IRowNode } from 'ag-grid-community';
import useDuplicateBulkAction from 'wb-spotify/app/routes/superGrid/actionRow/bulkActions/hooks/useDuplicateBulkAction';
import useDeleteBulkAction from 'wb-spotify/app/routes/superGrid/actionRow/bulkActions/hooks/useDeleteBulkAction';
import useStatusBulkAction from 'wb-spotify/app/routes/superGrid/actionRow/bulkActions/hooks/useStatusBulkAction';
import { SpotifyLevel } from 'wb-spotify/app/routes/superGrid/utils';
import useUserMetaData from 'wb-reddit/app/common/hooks/useUserMetaData';
import { CampaignStatus } from 'wb-spotify/graphql/generated';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isEntitySyncPending } from 'wb-spotify/app/routes/superGrid/cellRenderers/utils/status';

type SpotifySupergridBulkActionsProps = {
	gridApi: GridApi | null;
};

const MAX_DELETE_COUNT_CAMPAIGN = 20;
const MAX_DELETE_COUNT_GROUP = 100;

const ALLOWED_DELETE_LEVELS = [SpotifyLevel.CAMPAIGN, SpotifyLevel.GROUP, SpotifyLevel.AD];
const ALLOWED_STATUS_CHANGE_LEVELS = [SpotifyLevel.CAMPAIGN];

const SpotifySupergridBulkActions = ({
	gridApi,
}: SpotifySupergridBulkActionsProps) => {
	const navigate = useNavigate();
	const { t } = useTranslation();

	const isCompareDisabled = (rowNodes) => !rowNodes.every(rowNode => rowNode.data.level === rowNodes[0].data.level);
	const isDuplicateDisabled = (rowNodes) => (rowNodes?.length ?? 0) < 1
		|| !rowNodes.every(rowNode => rowNode.data.level === rowNodes[0].data.level)
    || rowNodes.some(rowNode => rowNode.data.level === SpotifyLevel.AD_ACCOUNT);

	const isDeleteDisabled = (rowNodes) => {
		// Only allow campaigns to be deleted, and max 20 at once
		if ((rowNodes?.length ?? 0) < 1) return true;
		if (
			rowNodes.filter(rowNode => rowNode.data.level === SpotifyLevel.CAMPAIGN).length > MAX_DELETE_COUNT_CAMPAIGN
				|| rowNodes.filter(rowNode => rowNode.data.level === SpotifyLevel.GROUP).length > MAX_DELETE_COUNT_GROUP
		) return true;

		if (rowNodes.some(rowNode => !ALLOWED_DELETE_LEVELS.includes(rowNode.data.level))) return true;

		return false;
	};

	const getDeleteMessage = (rowNodes: IRowNode[]) => {
		if (rowNodes.some(rowNode => !ALLOWED_DELETE_LEVELS.includes(rowNode.data.level))) {
			return t('sp: allowed_to_delete', { level: ALLOWED_DELETE_LEVELS.map((level) => `${level}s`).join(', ') });
		}

		if (rowNodes.filter(rowNode => rowNode.data.level === SpotifyLevel.CAMPAIGN).length > MAX_DELETE_COUNT_CAMPAIGN) {
			return t('sp: bulk_campaign_warn', { maxCampaigns: 20 });
		}

		if (rowNodes.filter(rowNode => rowNode.data.level === SpotifyLevel.GROUP).length > MAX_DELETE_COUNT_GROUP) {
			return t('sp: bulk_group_warn', { maxGroups: 100 });
		}

		return null;
	};

	// For now only allow status change for campaigns, what happens on the other level is still in discussion
	// See this slack thread https://alephholding.slack.com/archives/C046JEDA003/p1754311924794009
	const isStatusDisabled = (rowNodes) => (rowNodes?.length ?? 0) < 1
			|| !rowNodes?.every(
				rowNode => {
					if (!ALLOWED_STATUS_CHANGE_LEVELS.includes(rowNode.data.level)) {
						return false;
					}

					if (isEntitySyncPending(rowNode.data)) {
						return false;
					}

					return [CampaignStatus.Active, CampaignStatus.Paused].includes(rowNode.data.status);
				},
			);

	const platformUser = useUserMetaData(AppEnum.SPOTIFY);
	const { onDuplicate, loading: loadingDuplicate } = useDuplicateBulkAction();
	const { onDelete, loading: loadingDelete } = useDeleteBulkAction(gridApi);
	const { onChangeStatus, loading: loadingChange } = useStatusBulkAction(gridApi);

	const { params, setUrlParams } = useUrlParams({ navigate, useParams });

	const handleCompare = (rowNodes) => {
		setUrlParams({
			...params,
			level:   rowNodes?.[0]?.data?.level ?? SpotifyLevel.CAMPAIGN,
			filters: {
				...(params?.filters ?? {}),
				[LEVEL_ID_FILTER[rowNodes[0].data.level as SpotifyLevel]]: [{
					value: rowNodes.map(rowNode => rowNode.data.id),
				}],
			},
		});
	};

	const handleDuplicate = async (rowNodes) => {
		try {
			await onDuplicate(rowNodes);
		} catch (e) {
			alephToast(
				<NotificationToast
					type={NotificationType.Error}
					title={t('sc: duplication err', 'Duplication failed')}
				/>,
			);
		}
	};

	const CompareButton = ({ onClick, disabled }) => (
		<Tooltip
			content={t('sc: compare warning', 'Comparison of entities is only available on the same level.')}
			disabled={!disabled}
			className="z-modal"
		>
			<Button
				title={t('sc: compare', 'Compare')}
				leftNode={<Paragraph />}
				appearance={ButtonAppearance.OUTLINED}
				disabled={disabled}
				onClick={onClick}
			/>
		</Tooltip>
	);

	const DuplicateButton = ({ onClick, disabled, loading }) => (
		<Tooltip
			content={t('sc: duplicate warning', 'Only duplication of entities on the same level is supported.')}
			disabled={!disabled}
			className="z-modal"
		>
			<Button
				title={t('sc: duplicate', 'Duplicate')}
				leftNode={loading ? <LoaderBars className="animate-spin" /> : <Group />}
				appearance={ButtonAppearance.OUTLINED}
				disabled={disabled}
				onClick={onClick}
				loading={loading}
			/>
		</Tooltip>
	);

	const StatusButton = ({
		onClick, disabled, loading, rowNodes,
	}: {
		onClick: (rowNodes: IRowNode[]) => Promise<void>
		// disabled is injected by BulkActions, it is boolean type
		disabled: any;
		loading: boolean;
		rowNodes?: IRowNode[];
	}) => {
		const isPause = rowNodes?.every(
			rowNode => rowNode.data.status === GenericStatus.Active && !rowNode.data.statusPending,
		);
		const isActivate = rowNodes?.every(
			rowNode => [GenericStatus.Paused, GenericStatus.Archived].includes(rowNode.data.status)
				&& !rowNode.data.statusPending,
		);

		const isDisabled = disabled || (!isPause && !isActivate);

		return (
			<Tooltip
				content={t('sc: status change warning', 'Only entities with paused, active and archived status can be changed.')}
				disabled={!isDisabled}
				className="z-modal"
			>
				<Button
					title={isPause ? t('sc: pause', 'Pause') : t('sc: activate', 'Activate')}
					leftNode={
						loading ? <LoaderBars className="animate-spin" /> : (isPause ? <PauseIcon /> : <PlayIcon />)
					}
					appearance={ButtonAppearance.OUTLINED}
					disabled={isDisabled}
					// @ts-ignore: it's wrapped differently through BulkActions
					onClick={onClick}
					loading={loading}
				/>
			</Tooltip>
		);
	};

	const DeleteButton = ({
		onClick, disabled, loading, rowNodes = [],
	}: {
		onClick: (rowNodes: IRowNode[]) => Promise<void>;
		disabled: (rowNodes: IRowNode[]) => boolean;
		loading: boolean;
		rowNodes?: IRowNode[];
	}) => (
		<Tooltip
			content={getDeleteMessage(rowNodes)}
			disabled={!disabled}
			className="z-modal"
		>
			<Button
				title={t('sc: delete', 'Delete')}
				leftNode={loading ? <LoaderBars className="animate-spin" /> : <TrashCanIcon />}
				appearance={ButtonAppearance.OUTLINED}
				disabled={disabled}
				onClick={onClick}
				loading={loading}
			/>
		</Tooltip>
	);

	return (
		<div className="absolute w-full z-10 bg-white">
			<BulkActions gridApi={gridApi}>
				<CompareButton
					onClick={handleCompare}
					disabled={isCompareDisabled}
				/>

				{!platformUser?.readOnly
					&& <DuplicateButton
						onClick={handleDuplicate}
						disabled={isDuplicateDisabled}
						loading={loadingDuplicate}
					/>
				}

				{!platformUser?.readOnly
					&& <StatusButton
						onClick={onChangeStatus}
						disabled={isStatusDisabled}
						loading={loadingChange}
					/>
				}

				{!platformUser?.readOnly
					&& <DeleteButton
						onClick={onDelete}
						disabled={isDeleteDisabled}
						loading={loadingDelete}
					/>
				}
			</BulkActions>
		</div>
	);
};

export default SpotifySupergridBulkActions;
